/**
 * Authentication and user-related types
 */

import type { BaseEntity, UUID } from "./common.types";

// User roles in the system. Mirrors the Postgres `user_role` enum.
// MSR + CONCIERGE added 2026-05-18 as R8-001 (purely additive — no
// existing user has these roles yet; subsequent R8-00x migrations
// will tighten RLS to reference them).
export enum UserRole {
  SUPER_ADMIN = "superadmin",
  ADMIN = "admin",
  MANAGER = "manager",
  STAFF = "staff",
  READONLY = "readonly",
  PARTNER = "partner",
  FINANCE = "finance",
  SUPPORT_ADMIN = "support_admin",
  SUPPORT_AGENT = "support_agent",
  IT = "it",
  MSR = "msr",
  CONCIERGE = "concierge",
  // Attorney question routing: an outside counsel who answers state-licensed
  // questions. The DB user_role enum already carried 'attorney'; without it here
  // there was no way to assign the role, so list_attorney_candidates (which filters
  // role = 'attorney') always returned empty and coverage could never be configured.
  ATTORNEY = "attorney",
  // 2026-10-29 (RM/TM role setup, migrations 1240/1241): the sales field managers. A manager's access minus the
  // modules they do not use and event configuration -- see utils/fieldManagerAccess.ts.
  REGIONAL_MANAGER = "regional_manager",
  TERRITORY_MANAGER = "territory_manager",
}

// User profile
export interface Profile extends BaseEntity {
  user_id: UUID;
  email: string;
  full_name: string;
  role: UserRole;
  organization_id?: UUID;
  location_id?: UUID;
  // The profiles column is phone_number (QA and PROD); there is no `phone` column.
  phone_number?: string | null;
  avatar_url?: string;
  is_active: boolean;
  last_login?: string;
  // Set when an admin assigns a temporary password; forces a password change at next
  // login (AuthContext gates the app to /force-password-change until cleared).
  password_reset_required?: boolean;
  password_reset_required_at?: string | null;
}

// User invitation
export interface UserInvitation extends BaseEntity {
  email: string;
  role: UserRole;
  organization_id?: UUID;
  location_id?: UUID;
  invited_by: UUID;
  token: string;
  expires_at: string;
  accepted_at?: string;
  status: "pending" | "accepted" | "expired" | "cancelled";
}

// Login credentials
export interface LoginCredentials {
  email: string;
  password: string;
}

// Authentication response
export interface AuthResponse {
  user: Profile;
  session: {
    access_token: string;
    refresh_token: string;
    expires_in: number;
    expires_at: string;
  };
}

// Password reset request
export interface PasswordResetRequest {
  email: string;
}

// Password reset confirmation
export interface PasswordResetConfirmation {
  token: string;
  password: string;
}

// User permissions
export interface UserPermissions {
  can_create_organizations: boolean;
  can_manage_users: boolean;
  can_view_all_contacts: boolean;
  can_export_data: boolean;
  can_delete_records: boolean;
  can_manage_billing: boolean;
}

// Auth context state
export interface AuthState {
  user: Profile | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  permissions: UserPermissions | null;
}
